import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate } from "../format";
import { useToast } from "../toast";
import type { ChargerGroup, Location, StripeInfo, Tenant, TenantUser } from "../types";

type PermissionGroup = "operator" | "admin" | "finance" | "tariff_manager";
type PermissionScope = "view_revenue" | "manage_hardware" | "edit_tariffs" | "remote_actions";

const PERMISSION_LABELS: Record<PermissionScope, string> = {
  view_revenue: "View Revenue",
  manage_hardware: "Manage Hardware",
  edit_tariffs: "Edit Tariffs",
  remote_actions: "Remote Actions",
};

const PERMISSION_GROUPS: Record<PermissionGroup, { label: string; description: string; permissions: PermissionScope[] }> = {
  admin: {
    label: "Admin",
    description: "Full tenant access, including team and billing control.",
    permissions: ["view_revenue", "manage_hardware", "edit_tariffs", "remote_actions"],
  },
  operator: {
    label: "Operator",
    description: "Operational charger control without revenue or tariff changes.",
    permissions: ["manage_hardware", "remote_actions"],
  },
  finance: {
    label: "Finance",
    description: "Read and export billing/revenue data.",
    permissions: ["view_revenue"],
  },
  tariff_manager: {
    label: "Tariff Manager",
    description: "Create and update tariff configuration.",
    permissions: ["edit_tariffs"],
  },
};

const PERMISSION_GROUP_ORDER: PermissionGroup[] = ["admin", "operator", "finance", "tariff_manager"];
const PERMISSION_SCOPES: PermissionScope[] = ["view_revenue", "manage_hardware", "edit_tariffs", "remote_actions"];

function getPermissionGroupMeta(group: string) {
  const typed = group as PermissionGroup;
  return PERMISSION_GROUPS[typed] ?? {
    label: group,
    description: "Custom permission group.",
    permissions: [] as PermissionScope[],
  };
}

function toIdArray(value: unknown): number[] {
  const raw = Array.isArray(value) ? value : [value];
  const parsed = raw.map((entry) => Number(entry)).filter((entry) => Number.isInteger(entry) && entry > 0);
  return Array.from(new Set(parsed));
}

interface ScopeOption {
  id: number;
  label: string;
}

function scopeSummary(selectedIds: number[] | undefined, options: ScopeOption[], allLabel: string) {
  const ids = selectedIds ?? [];
  if (ids.length === 0) return allLabel;
  const byId = new Map(options.map((option) => [option.id, option.label]));
  const labels = ids.map((id) => byId.get(id)).filter(Boolean) as string[];
  if (labels.length === 0) return allLabel;
  return labels.join(", ");
}

// ── Invite user dialog ────────────────────────────────────────────────────────

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
  locationOptions: ScopeOption[];
  chargerGroupOptions: ScopeOption[];
}

function InviteDialog({ open, onClose, onInvited, locationOptions, chargerGroupOptions }: InviteDialogProps) {
  const authApi = useAuthApi();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [permissionGroup, setPermissionGroup] = useState<PermissionGroup>("operator");
  const [locationScopeIds, setLocationScopeIds] = useState<number[]>([]);
  const [chargerGroupScopeIds, setChargerGroupScopeIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail("");
    setPermissionGroup("operator");
    setLocationScopeIds([]);
    setChargerGroupScopeIds([]);
    setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi(`${MGMT}/users`, {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          permission_group: permissionGroup,
          location_scope_ids: locationScopeIds,
          charger_group_scope_ids: chargerGroupScopeIds,
        }),
      });
      toast.show("User added successfully", "success");
      reset();
      onInvited();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add team member</DialogTitle>
      <DialogContent>
        <Stack component="form" id="invite-form" spacing={2.5} sx={{ mt: 1 }} onSubmit={handleSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={loading}
            helperText="The user must have already signed up to Zennet."
          />
          <FormControl fullWidth disabled={loading}>
            <InputLabel>Permission Group</InputLabel>
            <Select
              value={permissionGroup}
              label="Permission Group"
              onChange={(e) => setPermissionGroup(e.target.value as PermissionGroup)}
            >
              {PERMISSION_GROUP_ORDER.map((group) => (
                <MenuItem key={group} value={group}>
                  {PERMISSION_GROUPS[group].label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, px: 1.5 }}>
              {getPermissionGroupMeta(permissionGroup).description}
            </Typography>
          </FormControl>
          <FormControl fullWidth disabled={loading}>
            <InputLabel>Location Scope</InputLabel>
            <Select
              multiple
              value={locationScopeIds}
              label="Location Scope"
              onChange={(e) => setLocationScopeIds(toIdArray(e.target.value))}
              renderValue={(selected) => scopeSummary(toIdArray(selected), locationOptions, "All locations")}
            >
              {locationOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={locationScopeIds.includes(option.id)} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, px: 1.5 }}>
              Leave empty for full tenant-wide location access.
            </Typography>
          </FormControl>
          <FormControl fullWidth disabled={loading}>
            <InputLabel>Charger Group Scope</InputLabel>
            <Select
              multiple
              value={chargerGroupScopeIds}
              label="Charger Group Scope"
              onChange={(e) => setChargerGroupScopeIds(toIdArray(e.target.value))}
              renderValue={(selected) => scopeSummary(toIdArray(selected), chargerGroupOptions, "All charger groups")}
            >
              {chargerGroupOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={chargerGroupScopeIds.includes(option.id)} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, px: 1.5 }}>
              Leave empty for full tenant-wide charger-group access.
            </Typography>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          type="submit"
          form="invite-form"
          variant="contained"
          disabled={loading || !email.trim()}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Add member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete tenant confirmation dialog ─────────────────────────────────────────

interface DeleteTenantDialogProps {
  tenant: Tenant;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteTenantDialog({ tenant, open, onClose, onDeleted }: DeleteTenantDialogProps) {
  const authApi = useAuthApi();
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await authApi(`${MGMT}/tenants/me`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: "error.main" }}>Delete organization</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="error">
            This will permanently delete <strong>{tenant.name}</strong> and remove all team members.
            This action cannot be undone.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={`Type "${tenant.slug}" to confirm`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            disabled={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={loading || confirm !== tenant.slug}
          onClick={handleDelete}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Delete organization"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const INTEGRATION_MODES = [
  { value: "invite_only", label: "Invite Only", description: "CPO controls access via invite rules. Drivers cannot self-register." },
  { value: "shared", label: "Shared", description: "All drivers are visible in anonymized form; self-registration is enabled." },
  { value: "isolated", label: "Isolated", description: "No eMSP integration. Drivers cannot self-register or share data." },
] as const;

// ── Main Team view ─────────────────────────────────────────────────────────────

export default function Team() {
  const authApi = useAuthApi();
  const toast = useToast();
  const { logout, user } = useAuth0();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [chargerGroups, setChargerGroups] = useState<ChargerGroup[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [me, setMe] = useState<TenantUser | null>(null);
  const [stripeInfo, setStripeInfo] = useState<StripeInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [visibilityMode, setVisibilityMode] = useState<string>("");
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [pncEnabled, setPncEnabled] = useState(false);
  const [pncUrl, setPncUrl] = useState("");
  const [pncRequestPath, setPncRequestPath] = useState("/contract-certificates/request");
  const [pncSignPath, setPncSignPath] = useState("/contract-certificates/sign");
  const [pncStatusPath, setPncStatusPath] = useState("/contract-certificates/status");
  const [pncApiKey, setPncApiKey] = useState("");
  const [pncSaving, setPncSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u, m, s, locs, groups] = await Promise.all([
        authApi<Tenant>(`${MGMT}/tenants/me`),
        authApi<TenantUser[]>(`${MGMT}/users`),
        authApi<TenantUser>(`${MGMT}/users/me`),
        authApi<StripeInfo>(`${MGMT}/tenants/me/billing`).catch(() => null),
        authApi<Location[]>(`${MGMT}/locations`),
        authApi<ChargerGroup[]>(`${MGMT}/charger-groups`),
      ]);
      setTenant(t);
      setUsers(u ?? []);
      setMe(m);
      setStripeInfo(s);
      setLocations(locs ?? []);
      setChargerGroups(groups ?? []);
      setVisibilityMode(t?.integration_mode ?? "invite_only");
      setPncEnabled(Boolean(t?.pnc_enabled));
      setPncUrl(t?.pnc_v2g_root_ca_url ?? "");
      setPncRequestPath(t?.pnc_v2g_root_ca_request_path ?? "/contract-certificates/request");
      setPncSignPath(t?.pnc_v2g_root_ca_sign_path ?? "/contract-certificates/sign");
      setPncStatusPath(t?.pnc_v2g_root_ca_status_path ?? "/contract-certificates/status");
      setPncApiKey("");
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleVisibilitySave = async () => {
    setVisibilitySaving(true);
    try {
      const updated = await authApi<Tenant>(`${MGMT}/tenants/me/integration-mode`, {
        method: "PATCH",
        body: JSON.stringify({ integration_mode: visibilityMode }),
      });
      if (updated) setTenant(updated);
      toast.show("Driver visibility updated", "success");
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    } finally {
      setVisibilitySaving(false);
    }
  };

  const handlePermissionGroupChange = async (userId: number, permissionGroup: string) => {
    try {
      await authApi(`${MGMT}/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ permission_group: permissionGroup }),
      });
      toast.show("Permission group updated", "success");
      load();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    }
  };

  const handleLocationScopeChange = async (userId: number, locationScopeIds: number[]) => {
    try {
      await authApi(`${MGMT}/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ location_scope_ids: locationScopeIds }),
      });
      toast.show("Location scope updated", "success");
      load();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    }
  };

  const handleChargerGroupScopeChange = async (userId: number, chargerGroupScopeIds: number[]) => {
    try {
      await authApi(`${MGMT}/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ charger_group_scope_ids: chargerGroupScopeIds }),
      });
      toast.show("Charger-group scope updated", "success");
      load();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    }
  };

  const handlePnCSave = async () => {
    if (!tenant) return;
    setPncSaving(true);
    try {
      const payload: Record<string, unknown> = {
        pnc_enabled: pncEnabled,
        pnc_v2g_root_ca_url: pncUrl.trim() || null,
        pnc_v2g_root_ca_request_path: pncRequestPath.trim() || null,
        pnc_v2g_root_ca_sign_path: pncSignPath.trim() || null,
        pnc_v2g_root_ca_status_path: pncStatusPath.trim() || null,
      };
      if (pncApiKey.trim().length > 0) {
        payload.pnc_v2g_root_ca_api_key = pncApiKey.trim();
      }
      const updated = await authApi<Tenant>(`${MGMT}/tenants/me/pnc-config`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (updated) {
        setTenant(updated);
        setPncEnabled(Boolean(updated.pnc_enabled));
        setPncUrl(updated.pnc_v2g_root_ca_url ?? "");
        setPncRequestPath(updated.pnc_v2g_root_ca_request_path ?? "/contract-certificates/request");
        setPncSignPath(updated.pnc_v2g_root_ca_sign_path ?? "/contract-certificates/sign");
        setPncStatusPath(updated.pnc_v2g_root_ca_status_path ?? "/contract-certificates/status");
      }
      setPncApiKey("");
      toast.show("Plug & Charge settings updated", "success");
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    } finally {
      setPncSaving(false);
    }
  };

  const handleRemove = async (u: TenantUser) => {
    if (!window.confirm(`Remove ${u.email ?? u.auth0_sub} from the organization?`)) return;
    try {
      await authApi(`${MGMT}/users/${u.id}`, { method: "DELETE" });
      toast.show("User removed", "success");
      load();
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      toast.show(msg, "error");
    }
  };

  const handleDeleted = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const stripeStatusColor = (s: string | null | undefined): "success" | "warning" | "error" | "default" => {
    if (s === "active") return "success";
    if (s === "past_due" || s === "unpaid") return "warning";
    if (s === "canceled") return "error";
    return "default";
  };

  const isAdmin = me?.permission_group === "admin";
  const locationOptions: ScopeOption[] = locations.map((location) => ({ id: location.id, label: location.name }));
  const chargerGroupOptions: ScopeOption[] = chargerGroups.map((group) => ({ id: group.id, label: group.name }));

  return (
    <>
      {/* Tenant info header */}
      {tenant && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Organization: <strong>{tenant.name}</strong>&nbsp;
            <Box component="span" sx={{ fontFamily: "monospace", color: "text.disabled" }}>
              ({tenant.slug})
            </Box>
          </Typography>
        </Box>
      )}

      {/* Billing info */}
      {stripeInfo && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Billing</Typography>
          {!stripeInfo.connected ? (
            <Chip label="No billing connected" color="default" variant="outlined" />
          ) : (
            <Stack spacing={1}>
              {stripeInfo.subscription_status && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                    Subscription status:
                  </Typography>
                  <Chip
                    label={stripeInfo.subscription_status}
                    color={stripeStatusColor(stripeInfo.subscription_status)}
                    size="small"
                  />
                  {stripeInfo.cancel_at_period_end && (
                    <Chip label="Cancels at period end" color="warning" size="small" />
                  )}
                </Box>
              )}
              {stripeInfo.current_period_end && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
                    Current period end:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(stripeInfo.current_period_end).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              {!stripeInfo.subscription_status && !stripeInfo.current_period_end && (
                <Typography variant="body2" color="text.secondary">
                  Connected — no active subscription found.
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
      )}

      {/* Driver Visibility */}
      {tenant && isAdmin && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Driver Visibility</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls how drivers are discovered and can join this organization.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={visibilitySaving}>
              <InputLabel>Visibility mode</InputLabel>
              <Select
                value={visibilityMode}
                label="Visibility mode"
                onChange={(e) => setVisibilityMode(e.target.value)}
              >
                {INTEGRATION_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={visibilitySaving || visibilityMode === tenant.integration_mode}
              onClick={handleVisibilitySave}
              sx={{ mt: 0.5 }}
            >
              {visibilitySaving ? <CircularProgress size={18} color="inherit" /> : "Save"}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {INTEGRATION_MODES.find((m) => m.value === visibilityMode)?.description}
          </Typography>
        </Paper>
      )}

      {/* Plug & Charge provider settings */}
      {tenant && isAdmin && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Plug & Charge (per tenant)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure this tenant's V2G CA provider. Disabled by default.
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch checked={pncEnabled} onChange={(e) => setPncEnabled(e.target.checked)} />}
              label="Enable Plug & Charge for this tenant"
            />
            <TextField
              label="V2G Root CA URL"
              size="small"
              value={pncUrl}
              onChange={(e) => setPncUrl(e.target.value)}
              placeholder="https://your-provider.example.com"
              disabled={pncSaving}
              fullWidth
              required={pncEnabled}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                label="Request path"
                size="small"
                value={pncRequestPath}
                onChange={(e) => setPncRequestPath(e.target.value)}
                disabled={pncSaving}
                fullWidth
              />
              <TextField
                label="Sign path"
                size="small"
                value={pncSignPath}
                onChange={(e) => setPncSignPath(e.target.value)}
                disabled={pncSaving}
                fullWidth
              />
              <TextField
                label="Status path"
                size="small"
                value={pncStatusPath}
                onChange={(e) => setPncStatusPath(e.target.value)}
                disabled={pncSaving}
                fullWidth
              />
            </Stack>
            <TextField
              label="V2G API key"
              size="small"
              type="password"
              value={pncApiKey}
              onChange={(e) => setPncApiKey(e.target.value)}
              placeholder={tenant.pnc_v2g_root_ca_api_key_set ? "•••••••• (stored)" : "Optional"}
              helperText={tenant.pnc_v2g_root_ca_api_key_set ? "Leave empty to keep existing key" : undefined}
              disabled={pncSaving}
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={handlePnCSave}
                disabled={pncSaving || (pncEnabled && !pncUrl.trim())}
              >
                {pncSaving ? <CircularProgress size={18} color="inherit" /> : "Save"}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Team members */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Access control
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permission groups define effective access scopes for users in this tenant.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Group</TableCell>
                <TableCell>Description</TableCell>
                {PERMISSION_SCOPES.map((scope) => (
                  <TableCell key={scope} align="center">{PERMISSION_LABELS[scope]}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PERMISSION_GROUP_ORDER.map((group) => {
                const meta = PERMISSION_GROUPS[group];
                return (
                  <TableRow key={group}>
                    <TableCell>
                      <Chip label={meta.label} size="small" color={group === "admin" ? "primary" : "default"} />
                    </TableCell>
                    <TableCell>{meta.description}</TableCell>
                    {PERMISSION_SCOPES.map((scope) => {
                      const enabled = meta.permissions.includes(scope);
                      return (
                        <TableCell key={`${group}-${scope}`} align="center">
                          <Chip
                            icon={enabled ? <CheckIcon /> : <CloseIcon />}
                            label={enabled ? "Yes" : "No"}
                            size="small"
                            color={enabled ? "success" : "default"}
                            variant={enabled ? "filled" : "outlined"}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Team members */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={600}>Team members</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Add member
            </Button>
          )}
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Permission Group</TableCell>
              <TableCell>Location Scope</TableCell>
              <TableCell>Charger Group Scope</TableCell>
              <TableCell>Effective Permissions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Added</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && users.map((u) => {
              const isSelf = u.auth0_sub === me?.auth0_sub || u.email === user?.email;
              const groupMeta = getPermissionGroupMeta(u.permission_group);
              return (
                <TableRow key={u.id} hover>
                  <TableCell>{u.email ?? "–"}</TableCell>
                  <TableCell>{u.name ?? "–"}</TableCell>
                  <TableCell>
                    {isAdmin && !isSelf ? (
                      <Select
                        value={u.permission_group}
                        size="small"
                        onChange={(e) => handlePermissionGroupChange(u.id, e.target.value)}
                        sx={{ minWidth: 150 }}
                      >
                        {PERMISSION_GROUP_ORDER.map((group) => (
                          <MenuItem key={group} value={group}>{PERMISSION_GROUPS[group].label}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        label={groupMeta.label}
                        size="small"
                        color={u.permission_group === "admin" ? "primary" : "default"}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isAdmin && !isSelf ? (
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          multiple
                          value={u.location_scope_ids ?? []}
                          onChange={(e) => handleLocationScopeChange(u.id, toIdArray(e.target.value))}
                          renderValue={(selected) => scopeSummary(toIdArray(selected), locationOptions, "All locations")}
                        >
                          {locationOptions.map((option) => (
                            <MenuItem key={`loc-scope-${u.id}-${option.id}`} value={option.id}>
                              <Checkbox checked={(u.location_scope_ids ?? []).includes(option.id)} />
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={scopeSummary(u.location_scope_ids, locationOptions, "All locations")}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isAdmin && !isSelf ? (
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          multiple
                          value={u.charger_group_scope_ids ?? []}
                          onChange={(e) => handleChargerGroupScopeChange(u.id, toIdArray(e.target.value))}
                          renderValue={(selected) => scopeSummary(toIdArray(selected), chargerGroupOptions, "All charger groups")}
                        >
                          {chargerGroupOptions.map((option) => (
                            <MenuItem key={`group-scope-${u.id}-${option.id}`} value={option.id}>
                              <Checkbox checked={(u.charger_group_scope_ids ?? []).includes(option.id)} />
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={scopeSummary(u.charger_group_scope_ids, chargerGroupOptions, "All charger groups")}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {groupMeta.permissions.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No mapped permissions</Typography>
                      ) : groupMeta.permissions.map((permission) => (
                        <Chip key={`${u.id}-${permission}`} label={PERMISSION_LABELS[permission]} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.status}
                      size="small"
                      color={u.status === "active" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>{fmtDate(u.created_at)}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip title={isSelf ? "Cannot remove yourself" : "Remove"}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isSelf}
                            onClick={() => handleRemove(u)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Danger zone */}
      {isAdmin && tenant && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={600} color="error.main" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Paper variant="outlined" sx={{ p: 3, borderColor: "error.light" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography fontWeight={500}>Delete organization</Typography>
                <Typography variant="body2" color="text.secondary">
                  Permanently deletes <strong>{tenant.name}</strong> and removes all team members.
                  You will be signed out.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteOpen(true)}
                sx={{ ml: 2, flexShrink: 0 }}
              >
                Delete organization
              </Button>
            </Box>
          </Paper>
        </>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => { setInviteOpen(false); load(); }}
        locationOptions={locationOptions}
        chargerGroupOptions={chargerGroupOptions}
      />

      {tenant && (
        <DeleteTenantDialog
          tenant={tenant}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
}
